<template>
  <div class="trigger" :class="{ mobile: isMobile }">
    <h6 v-if="!(isEditingAlert && isMobile)">
      <span v-if="isMobile" @click="backToIndexPage">
        <fa icon="chevron-left" class="icon"></fa>
      </span>
      Trigger
    </h6>
    <p>Select alert trigger type (ROA/Route object alignment or BGP status):</p>
    <span v-if="isMobile" class="separate-line"></span>
    <div class="trigger-type">
      <div class="form-check">
        <input
          type="radio"
          class="form-check-input"
          id="ROA-route-object-alignment"
          :value="TRIGGER_TYPE.ROA_OR_ROUTE_OBJECT_ALIGNMENT"
          v-model="triggerObj.triggerType"
        />
        <label for="ROA-route-object-alignment" class="form-check-label"
          >ROA/Route object alignment</label
        >
      </div>
      <div class="form-check">
        <input
          type="radio"
          class="form-check-input"
          :disabled="isBGPStatusDisabled"
          id="BGP-announcement-status"
          :value="TRIGGER_TYPE.BGP_ANNOUNCEMENT_STATUS"
          v-model="triggerObj.triggerType"
        />
        <label for="BGP-announcement-status" class="form-check-label"
          >BGP status</label
        >
      </div>
    </div>
    <template
      v-if="
        triggerObj.triggerType === TRIGGER_TYPE.ROA_OR_ROUTE_OBJECT_ALIGNMENT
      "
    >
      <div class="trigger-option-container">
        <h6>
          ROA/Route object alignment
        </h6>
        <p class="with-asterisk">Select trigger status:</p>
        <div
          v-if="!triggerObj.alignmentType.length"
          class="warning-description text-danger"
        >
          A selection is required.
        </div>
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            :value="ALIGNMENT_TYPE.MISMATCHED"
            v-model="triggerObj.alignmentType"
            id="mismatch"
          />
          <label class="form-check-label" for="mismatch">
            Mismatch <span>(against ROAs or Route objects)</span>
          </label>
        </div>
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            :value="ALIGNMENT_TYPE.NOT_PUBLISHED"
            v-model="triggerObj.alignmentType"
            id="not-published"
          />
          <label class="form-check-label" for="not-published">
            Not published <span>(ROA or Route object)</span>
          </label>
        </div>
      </div>
    </template>
    <template
      v-if="triggerObj.triggerType === TRIGGER_TYPE.BGP_ANNOUNCEMENT_STATUS"
    >
      <div class="trigger-option-container">
        <h6>
          BGP status
        </h6>
        <p class="title">BGP announcement status:</p>
        <div>
          <div class="form-check">
            <input
              type="radio"
              class="form-check-input"
              id="route-exists"
              :value="BGP_ANNOUNCEMENT_TYPE.ROUTE_EXISTS"
              v-model="triggerObj.BGPAnnouncementType"
            />
            <label for="route-exists" class="form-check-label"
              >Route exists</label
            >
          </div>
          <div class="form-check">
            <input
              type="radio"
              class="form-check-input"
              id="route-not-exists"
              :value="BGP_ANNOUNCEMENT_TYPE.ROUTE_NOT_EXISTS"
              v-model="triggerObj.BGPAnnouncementType"
            />
            <label for="route-not-exists" class="form-check-label"
              >Route doesn’t exist</label
            >
          </div>
        </div>
        <p class="title">Select origin AS:</p>
        <div>
          <div class="form-check">
            <input
              type="radio"
              class="form-check-input"
              id="any-delegated"
              :value="SELECTED_ORIGIN_AS_TYPE.ANY_DELEGATED_TO_ACCOUNT"
              v-model="triggerObj.selectedOriginASType"
            />
            <label for="any-delegated" class="form-check-label"
              >Any origin AS delegated to your account.</label
            >
          </div>
          <div class="form-check">
            <input
              type="radio"
              class="form-check-input"
              id="any-not-delegated"
              :value="SELECTED_ORIGIN_AS_TYPE.ANY_NOT_DELEGATED_TO_ACCOUNT"
              v-model="triggerObj.selectedOriginASType"
            />
            <label for="any-not-delegated" class="form-check-label"
              >Any origin AS not delegated to your account.</label
            >
          </div>
          <div class="form-check">
            <input
              type="radio"
              class="form-check-input"
              id="individual"
              :value="SELECTED_ORIGIN_AS_TYPE.INDIVIDUAL"
              v-model="triggerObj.selectedOriginASType"
            />
            <label for="individual" class="form-check-label"
              >Select individual Origin ASNs.</label
            >
          </div>
        </div>
        <div
          v-if="
            triggerObj.selectedOriginASType ===
              SELECTED_ORIGIN_AS_TYPE.INDIVIDUAL
          "
          class="individual-AS-container"
        >
          <div class="input-container">
            <input
              type="text"
              class="form-control"
              :class="{ warning: !!errorMessageAboutInputtedOriginAS }"
              placeholder="Type origin AS number"
              v-model.trim="inputtedOriginAS"
              @keydown="errorMessageAboutInputtedOriginAS = ''"
              @keyup.enter="addInputtedOriginASIntoList"
            />
            <button
              class="btn btn-primary btn-sm"
              :disabled="!!errorMessageAboutInputtedOriginAS"
              @click="addInputtedOriginASIntoList"
            >
              Add
            </button>
          </div>
          <div v-if="!!errorMessageAboutInputtedOriginAS" class="warning-text">
            {{ errorMessageAboutInputtedOriginAS }}
          </div>
          <div class="list-container">
            <p class="title with-asterisk">Selected Origin ASNs:</p>
            <div
              v-if="
                (isInvalidInputWarningShown || isEditingAlert) &&
                  !triggerObj.selectedOriginASList.length
              "
              class="warning-description text-danger"
            >
              At least one individual origin AS must be selected.
            </div>
            <div class="list" v-if="triggerObj.selectedOriginASList.length">
              <ul>
                <li
                  v-for="(item, index) in triggerObj.selectedOriginASList"
                  :key="item + index"
                >
                  <span>{{ item }}</span>
                  <div
                    class="icon-container"
                    @click="removeASNumber(index)"
                    v-tooltip.top="{
                      content: tooltipText
                    }"
                  >
                    <fa icon="minus-circle" class="icon"></fa>
                  </div>
                </li>
              </ul>
            </div>
            <div class="list" v-else>
              <span class="empty-list">
                (No origin AS added)
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import VueCustomScrollbar from 'vue-custom-scrollbar';
import { mapGetters, mapState } from 'vuex';

import {
  TRIGGER_TYPE,
  ALIGNMENT_TYPE,
  BGP_ANNOUNCEMENT_TYPE,
  SELECTED_ORIGIN_AS_TYPE
} from '../../../constants/alertsRoutingStatusConstant';
import { checkIsASNumber, normalizeASN } from '../../../utils/ipAddressHelpers';

export default {
  components: { VueCustomScrollbar },
  name: 'TriggerRS',
  props: ['triggerObj', 'isBGPStatusDisabled', 'isInvalidInputWarningShown'],
  data() {
    return {
      TRIGGER_TYPE: TRIGGER_TYPE,
      ALIGNMENT_TYPE: ALIGNMENT_TYPE,
      BGP_ANNOUNCEMENT_TYPE: BGP_ANNOUNCEMENT_TYPE,
      SELECTED_ORIGIN_AS_TYPE: SELECTED_ORIGIN_AS_TYPE,
      inputtedOriginAS: '',
      errorMessageAboutInputtedOriginAS: '',
      tooltipText: 'Remove origin AS'
    };
  },
  computed: {
    ...mapGetters(['isMobile']),
    ...mapState('alertsRoutingStatusStore', ['isEditingAlert'])
  },
  watch: {},
  mounted() {},
  methods: {
    backToIndexPage() {
      this.$emit('backToIndexPage');
    },

    addInputtedOriginASIntoList() {
      const isValidASNumber = checkIsASNumber(this.inputtedOriginAS);

      if (isValidASNumber) {
        this.inputtedOriginAS = normalizeASN(
          this.inputtedOriginAS.toUpperCase()
        );

        const isExistingResource = this.triggerObj.selectedOriginASList.includes(
          this.inputtedOriginAS
        );

        if (!isExistingResource) {
          this.triggerObj.selectedOriginASList.push(this.inputtedOriginAS);
          this.inputtedOriginAS = '';
        } else {
          // Show error message: duplicated AS number
          this.errorMessageAboutInputtedOriginAS =
            'AS number has been selected.';
        }
      } else {
        // Show error message: invalid AS number
        this.errorMessageAboutInputtedOriginAS =
          'Please enter a valid AS number.';
      }
    },

    removeASNumber(index) {
      this.triggerObj.selectedOriginASList.splice(index, 1);
    }
  }
};
</script>

<style lang="scss" scoped>
.trigger {
  position: relative;
  padding: 2rem 1.5rem;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;

  & > p {
    line-height: 1.5rem;
    margin-bottom: 0;
  }
}

h6 {
  font-weight: 600;

  & > span {
    font-size: 1.2rem;
    padding: 0.5rem 1rem;
    margin-left: -1rem;
    vertical-align: bottom;
    color: $text-light-blue;
  }
}

.separate-line {
  @include separate-line;
}

.trigger-type {
  display: flex;
  margin: 0.5rem 0;

  & > div {
    margin-right: 4rem;
    padding: 0.25rem 0 0.25rem 1.5rem;
  }
}

.trigger-option-container {
  padding: 1rem;
  border: 1px solid #606060;

  & > h6 {
    margin-bottom: 1rem;
  }

  .title {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }

  .form-check {
    margin-left: 1rem;
    margin-bottom: 0.5rem;
  }

  .warning-description {
    margin-top: -0.5rem;
    margin-bottom: 0.25rem;
    margin-left: 1rem;
    font-size: 0.75rem;
  }
}

.individual-AS-container {
  width: 66%;
  min-width: 20rem;
  margin-left: 2rem;

  .input-container {
    position: relative;

    .warning {
      color: $warning-red;
      box-shadow: none;
      border-color: $warning-red;
    }

    button {
      position: absolute;
      top: 0;
      right: 0;
      max-width: 4rem;
      min-width: 4rem;
      height: 2rem;
      margin-top: 0.2rem;
      margin-right: 0.2rem;
    }
  }

  .warning-text {
    position: absolute;
    color: $warning-red;
    font-size: 0.75rem;
  }

  .list-container {
    .list {
      min-height: 2rem;
      border: 1px solid $text-grey;
    }

    ul {
      list-style-type: none;
      margin: 0;

      li {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem;
        padding-left: 0.75rem;

        &:hover {
          background-color: #d9d9d9;
        }

        &:first-of-type {
          padding-top: 0.75rem;
        }

        &:last-of-type {
          padding-bottom: 0.75rem;
        }

        .icon-container {
          flex: 0 0 2rem;
          width: 1rem;
          padding: 0 0.5rem;

          svg {
            color: $icon-grey;
          }

          &:hover {
            cursor: pointer;

            svg {
              color: black;
            }
          }
        }
      }
    }

    .empty-list {
      display: inline-block;

      padding: 0.5rem;
      color: #7f7f7f;
      font-style: italic;
    }

    .warning-description {
      margin: 0;
      font-size: 0.75rem;
    }
  }
}

.mobile {
  padding: 1rem;

  h6 {
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 1rem;
  }

  .trigger-type {
    flex-direction: column;
    margin-top: -0.5rem;
  }

  .trigger-option-container,
  .warning-description,
  .form-check {
    margin-left: 0;

    label {
      span {
        display: block;
      }
    }
  }

  .individual-AS-container {
    margin-left: 0;
    width: 100%;
    min-width: 100%;
  }
}
</style>
